import React, { ChangeEvent, useEffect, useState } from "react";
import AppBar from "../components/AppBar";
import CustomInput from "../components/CustomInput";
import CustomRadioButton from "../components/CustomRadioButton";
import CustomSelect from "../components/CustomSelect";
import FormSection, {
  FieldLabel,
  dashedButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../components/form-section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCircleNotch,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import DriverForm from "../components/createChaseComponent/driverForm";
import GenerateRandomId from "../utils/generateRandomId";
import { InitialDriverInterface } from "../types/driver-type";
import { InsuranceProviderInterface } from "../types/provider-types";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { providerPermissionFields } from "../components/providerComponents/providersData";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import GenderData from "../utils/data/genderData";
import { chaseStatusData } from "../components/chaseListComponents/chaseListData";

interface EXInsuranceProviderInterface extends InsuranceProviderInterface {
  label: string;
}

const CreateChase: React.FC = () => {
  const [values, setValues] = useState<Record<string, any>>({
    driver: "Main Driver",
    chaseType: "Sales",
    policyType: "Standard",
    paymentType: "Cash",
    status: "Awaiting-Customer",
    drivers: [],
  });
  const [providers, setProviders] = useState<EXInsuranceProviderInterface[]>(
    []
  );
  const [drivers, setDrivers] = useState<InitialDriverInterface[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [providerSearch, setProviderSearch] = useDebounceValue("", 300);
  const [pLoading, setPLoading] = useState<boolean>(false);

  // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

  const createChase = async () => {
    if (validateChaseForm()) {
      setLoading(true);
      try {
        const res = await axiosInstance.post("/chase", {
          ...values,
          policyReferenceNumber: values?.policyReferenceNumber?.replaceAll(
            " ",
            ""
          ),
        });
        toast.success(res.data?.message);
        navigate("/chaselist");
        setLoading(false);
      } catch (error) {
        toast.error(ParseErrors(error));
        setLoading(false);
        console.log(error);
      }
    }
  };

  // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    200
  );

  const SelectProvider = (option: EXInsuranceProviderInterface) => {
    if (option?.id) {
      setValues((prev) => ({
        ...prev,
        provider: option?.label,
        insurerId: option?.id,
        chaseItems: providerPermissionFields.filter(
          (provFields) =>
            option[provFields.id] === "Additional Driver" ||
            option[provFields.id] === "Main Driver"
        ),
        policyType: option?.product,
        insurerSchemeCode: option?.schemeCode,
        chaseType: option?.type,
      }));
    }
  };

  // --------------------------------------- [ DRIVERS FUNCTIONS ] -----------------------------------

  const AddNewDriver = () => {
    setDrivers((prev) => [
      ...prev,
      { rId: GenerateRandomId(), chaseType: values.chaseType },
    ]);
  };

  const RemoveDriver = (rId: string) => {
    setDrivers(drivers.filter((item) => item.rId !== rId));
  };

  const fetchDrivers = (driversData: Record<string, any>) => {
    const isDriver = values?.drivers?.find(
      (driver) => driver?.rId === driversData?.rId
    );
    if (!isDriver) {
      setValues((prev) => ({
        ...prev,
        drivers: [...prev.drivers, driversData],
      }));
    } else {
      const drivers = values?.drivers.filter(
        (item) => item.rId !== driversData?.rId
      );
      setValues((prev) => ({ ...prev, drivers: [...drivers, driversData] }));
    }
  };

  // -------------------------------- [ FIND PROVIDERS ] --------------------------------------

  useEffect(() => {
    const findProviders = async () => {
      setPLoading(true);
      try {
        const providerRes = await axiosInstance.get(
          providerSearch ? `/provider?search=${providerSearch}` : "/provider"
        );
        setProviders(
          providerRes.data.data?.map((providerItem) => ({
            ...providerItem,
            label: `${providerItem.name} (${providerItem.type} - ${providerItem?.productType}/${providerItem?.schemeCode})`,
          }))
        );
        setPLoading(false);
      } catch (error) {
        ParseErrors(error);
        setPLoading(false);
      }
    };

    findProviders();
  }, [providerSearch]);

  // ----------------------------------- [ VALIDATE FUNCTIONS ] --------------------------------

  const validateChaseForm = () => {
    const isDriverLicenseChased =
      values.chaseItems?.find(
        (chaseItm) => chaseItm?.id === "driversLicenseSummary"
      ) ||
      values.drivers.find((driver: any) =>
        driver?.chaseItems?.find(
          (driverChaseItm) => driverChaseItm?.id === "driversLicenseSummary"
        )
      );

    if (!values?.firstName) {
      toast.error("Please enter First Name");
      return false;
    } else if (!values?.surname) {
      toast.error("Please enter Surname");
      return false;
    } else if (!values?.regNo) {
      toast.error("Please enter Registration No");
      return false;
    } else if (!values?.email) {
      toast.error("Please enter Email");
      return false;
    } else if (isDriverLicenseChased && !values?.gender) {
      toast.error("Please select your gender");
      return false;
    } else if (isDriverLicenseChased && !values?.dob) {
      toast.error("Please enter Date of Birth");
      return false;
    } else if (!values?.phone) {
      toast.error("Please enter Phone");
      return false;
    } else if (!values?.policyReferenceNumber) {
      toast.error("Please enter Policy Reference Number");
      return false;
    } else if (!values?.driver) {
      toast.error("Please Select Driver Type");
      return false;
    } else if (!values?.policyType) {
      toast.error("Please Select Policy Type");
      return false;
    } else if (!values?.chaseType) {
      toast.error("Please Select Chase Department");
      return false;
    } else if (values?.provider === "Close Brothers" && !values?.postCode) {
      toast.error("Post Code is required");
      return false;
    } else if (!ValidateDrivers()) {
      return false;
    } else {
      return true;
    }
  };

  const ValidateDrivers = () => {
    let driver = values?.drivers?.find(
      (driver) =>
        !driver.name ||
        !driver.policyType ||
        !driver.chaseType ||
        !driver?.chaseItems?.length
    );

    if (!driver) {
      return true;
    }

    if (!driver?.name) {
      toast.error("Please enter driver name");
      return false;
    } else if (!driver?.chaseItems?.length) {
      toast.error(`Chase Items for ${driver?.name} is required`);
      return false;
    } else {
      return true;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 w-full border-b border-edge-faint z-20">
        <AppBar title="DocuSure" profileInfo />
      </div>

      <div className="mx-auto w-full max-w-[880px] px-4 sm:px-6 py-8 smallTablet:py-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={secondaryButtonClass}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-[12px]" />
          Back
        </button>

        <div className="mt-4 bg-surface border border-edge rounded-2xl shadow-card animate-fade-in">
          {/* ---------------------------------- [ HEADER ] ---------------------------------- */}
          <div className="px-6 sm:px-8 py-5 smallTablet:px-4 smallTablet:py-4 border-b border-edge-faint">
            <h1 className="text-[22px] mobile:text-[18px] font-bold text-text-primary leading-tight">
              Create chase
            </h1>
            <p className="mt-1 text-[13px] font-medium text-light-gray">
              Set up a new document chase and choose what the customer needs to
              provide.
            </p>
          </div>

          {/* --------------------------------- [ CUSTOMER ] --------------------------------- */}
          <FormSection title="Customer">
            <CustomInput
              autoComplete="none"
              label="First name"
              defaultValue={values?.firstName}
              name="firstName"
              onChange={HandleChangeValues}
            />
            <CustomInput
              autoComplete="none"
              label="Surname"
              defaultValue={values?.surname}
              name="surname"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Email address"
              autoComplete="none"
              defaultValue={values?.email}
              name="email"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Phone number"
              autoComplete="none"
              defaultValue={values?.phone}
              name="phone"
              onChange={HandleChangeValues}
            />
            <CustomInput
              type="date"
              label="Date of birth"
              autoComplete="none"
              defaultValue={values?.dob}
              name="dob"
              onChange={HandleChangeValues}
            />
            <CustomSelect
              comboboxType="select"
              options={GenderData}
              labelProperty="label"
              valueProperty="id"
              label="Gender"
              placeholder="Select gender"
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, gender: option.id }))
              }
              value={GenderData.find((gender) => gender.id === values?.gender)}
            />
          </FormSection>

          {/* ---------------------------------- [ POLICY ] ---------------------------------- */}
          <FormSection title="Policy">
            <CustomInput
              label="Policy reference number"
              autoComplete="none"
              defaultValue={values?.policyReferenceNumber}
              name="policyReferenceNumber"
              onChange={HandleChangeValues}
            />
            <CustomInput
              type="text"
              label="Registration no"
              autoComplete="none"
              defaultValue={values?.regNo}
              name="regNo"
              onChange={HandleChangeValues}
            />
            <CustomSelect
              comboboxType="select"
              label="Chase status"
              labelProperty="name"
              showTop
              valueProperty="name"
              options={chaseStatusData}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, status: option?.name }))
              }
              value={chaseStatusData.find(
                (type) => type.name === values?.status
              )}
            />
            <CustomInput
              label="Agent code"
              autoComplete="none"
              defaultValue={values?.customerExCode}
              name="customerExCode"
              onChange={HandleChangeValues}
            />

            <div className="sm:col-span-2">
              <CustomSelect
                comboboxType="select"
                options={providers}
                labelProperty="label"
                valueProperty="id"
                label="Provider"
                placeholder="Select provider"
                onSingleSelectChange={(option: EXInsuranceProviderInterface) => {
                  SelectProvider(option);
                }}
                value={providers.find(
                  (provData) => provData.id === values?.insurerId
                )}
                onInputChange={(value) => setProviderSearch(value)}
                isLoading={pLoading}
              />
            </div>

            {values?.provider === "Close Brothers" && (
              <CustomInput
                label="Post code"
                autoComplete="none"
                defaultValue={values?.postCode}
                name="postCode"
                onChange={HandleChangeValues}
              />
            )}
          </FormSection>

          {/* -------------------------------- [ CHASE ITEMS ] -------------------------------- */}
          <FormSection
            title="Chase items"
            description="The documents the customer will be asked to provide."
          >
            <div className="sm:col-span-2">
              <CustomSelect
                comboboxType="select"
                options={providerPermissionFields}
                labelProperty="label"
                valueProperty="id"
                label="Chase items"
                placeholder="Select items"
                onMultiSelectChange={(option: typeof providerPermissionFields) =>
                  setValues((prev) => ({ ...prev, chaseItems: [...option] }))
                }
                value={values?.chaseItems || []}
                multiple
              />
            </div>
          </FormSection>

          {/* -------------------------------- [ PREFERENCES ] -------------------------------- */}
          <FormSection title="Preferences">
            <div>
              <FieldLabel>Policy type</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                <CustomRadioButton
                  id="standard"
                  name="policyType"
                  label="Standard"
                  checked={values?.policyType === "Standard"}
                  onChange={HandleChangeValues}
                  value="Standard"
                />
                <CustomRadioButton
                  id="Nonstandard"
                  name="policyType"
                  label="Non standard"
                  value="Non-Standard"
                  checked={values?.policyType === "Non-Standard"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div>
              <FieldLabel>Department</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                <CustomRadioButton
                  id="sales"
                  name="chaseType"
                  label="Sales"
                  checked={values?.chaseType === "Sales"}
                  onChange={HandleChangeValues}
                  value="Sales"
                />
                <CustomRadioButton
                  id="Renewals"
                  name="chaseType"
                  label="Renewals"
                  value="Renewals"
                  checked={values?.chaseType === "Renewals"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div>
              <FieldLabel>Payment type</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                <CustomRadioButton
                  id="cash"
                  name="paymentType"
                  label="Cash"
                  checked={values?.paymentType === "Cash"}
                  onChange={HandleChangeValues}
                  value="Cash"
                />
                <CustomRadioButton
                  id="instalments"
                  name="paymentType"
                  label="Instalments"
                  value="Instalments"
                  checked={values?.paymentType === "Instalments"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div>
              <FieldLabel>Driver</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                <CustomRadioButton
                  id="mainDriver"
                  label="Main driver"
                  name="driver"
                  checked={values?.driver === "Main Driver"}
                  onChange={HandleChangeValues}
                  value="Main Driver"
                />
                <CustomRadioButton
                  id="additionalDriver"
                  label="Additional driver"
                  name="driver"
                  value="Additional Driver"
                  checked={values?.driver === "Additional Driver"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>
          </FormSection>

          {/* --------------------------------- [ DRIVERS ] --------------------------------- */}
          <FormSection
            title="Additional drivers"
            description="Chase documents from anyone else named on the policy."
            className="!grid-cols-1"
          >
            {drivers.map((item) => {
              return (
                <DriverForm
                  key={item.rId}
                  item={item}
                  RemoveDriver={RemoveDriver}
                  providerDetails={providers.find(
                    (item) => item.name === values.provider
                  )}
                  fetchDrivers={fetchDrivers}
                />
              );
            })}

            <button
              type="button"
              onClick={AddNewDriver}
              disabled={loading}
              className={dashedButtonClass}
            >
              <FontAwesomeIcon icon={faPlus} className="text-[12px]" />
              Add additional driver
            </button>
          </FormSection>

          {/* ---------------------------------- [ FOOTER ] ---------------------------------- */}
          <div className="px-6 sm:px-8 py-5 smallTablet:px-4 smallTablet:py-4 border-t border-edge-faint flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className={secondaryButtonClass}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={createChase}
              disabled={loading}
              className={`${primaryButtonClass} sm:ml-auto sm:min-w-[180px]`}
            >
              {loading ? (
                <FontAwesomeIcon
                  icon={faCircleNotch}
                  className="fa-spin text-[14px]"
                />
              ) : (
                "Create chase"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateChase;
